import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CheckCircle2, CreditCard, Inbox, Loader2, Search, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useUiLanguage } from '@/lib/i18n';
import useStore from '@/store/useStore';
import CustomerCombobox from '@/components/merchant/CustomerCombobox';
import {
  apiMerchantCampaignDetail,
  apiMerchantCampaigns,
  apiMerchantDashboard,
  apiMerchantPendingRedemptions,
  apiRejectRedemption,
  apiSimulateOrder,
  apiValidateRedemption,
} from '@/services/api';
import { AI_ACTION_TYPES, executeAIAction } from '@/lib/aiActionEngine';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function resolveBestBenefit(customer, promo) {
  if (!customer) return null;
  const points = Math.max(Number(customer?.points || 0), 0);
  const rewardTiers = [
    { points: 2500, amount: 25 },
    { points: 1000, amount: 10 },
    { points: 500, amount: 5 },
    { points: 300, amount: 3 },
  ];
  for (const tier of rewardTiers) {
    if (points >= tier.points) {
      return {
        kind: 'reward',
        title: `$${tier.amount} Cashback Reward`,
        subtitle: `Uses ${tier.points} points instantly`,
        points: tier.points,
      };
    }
  }

  if (promo?.code) {
    const quickPoints = points >= 100 ? 100 : 0;
    return {
      kind: 'promo',
      title: `Promo available (${promo.code})`,
      subtitle: quickPoints
        ? 'Quick redeem uses 100 points now. Promo remains for guest checkout.'
        : 'Promo detected. Guest can redeem promo in app checkout.',
      points: quickPoints,
      promoCode: promo.code,
    };
  }

  if (points >= 100) {
    return {
      kind: 'points',
      title: '$1 Points Redemption',
      subtitle: 'Uses 100 points instantly',
      points: 100,
    };
  }

  return null;
}

export default function RedemptionsPage() {
  const navigate = useNavigate();
  const { openEarnModal } = useStore();
  const { t } = useUiLanguage();

  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [promoByCustomer, setPromoByCustomer] = useState({});
  const [busyAction, setBusyAction] = useState(false);
  const [busySimulate, setBusySimulate] = useState(false);
  const [applyBonusOnRedemption, setApplyBonusOnRedemption] = useState(false);
  const [toast, setToast] = useState('');
  const [selectedSimulateCustomerId, setSelectedSimulateCustomerId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [pending, setPending] = useState([]);
  const [rejectedPendingIds, setRejectedPendingIds] = useState({});
  const [busyQueueAction, setBusyQueueAction] = useState('');

  const campaignListRef = useRef(null);
  const campaignDetailCacheRef = useRef({});
  const toastTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const queue = useMemo(
    () => pending.filter((row) => !rejectedPendingIds[row.id]),
    [pending, rejectedPendingIds]
  );

  const setToastMessage = (message) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(message);
    toastTimerRef.current = window.setTimeout(() => setToast(''), 3800);
  };

  const fetchCustomers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [dashboardRes, pendingRes] = await Promise.allSettled([
        apiMerchantDashboard(),
        apiMerchantPendingRedemptions(),
      ]);
      if (dashboardRes.status !== 'fulfilled') {
        throw dashboardRes.reason;
      }
      const dashboard = dashboardRes.value;
      if (!mountedRef.current) return;
      const rows = Array.isArray(dashboard?.customers) ? dashboard.customers : [];
      setCustomers(rows);
      if (pendingRes.status === 'fulfilled') {
        setPending(Array.isArray(pendingRes.value) ? pendingRes.value : []);
      }
      if (!selectedCustomerId && rows.length) {
        setSelectedCustomerId(rows[0].id);
      }
      if (!selectedSimulateCustomerId && rows.length) {
        setSelectedSimulateCustomerId(rows[0].id);
      }
    } catch {
      setToastMessage(t('Unable to load customers right now.'));
    }
    if (!silent && mountedRef.current) setLoading(false);
  }, [selectedCustomerId, selectedSimulateCustomerId, t]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchCustomers(true);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [fetchCustomers]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!searchPhone.trim()) return;
    const timer = window.setTimeout(() => {
      fetchCustomers(true);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [fetchCustomers, searchPhone]);

  const matchedCustomer = useMemo(() => {
    const searchDigits = onlyDigits(searchPhone);
    if (searchDigits) {
      return (
        customers.find((c) => onlyDigits(c?.phone).includes(searchDigits)) ||
        null
      );
    }
    if (selectedCustomerId) {
      return customers.find((c) => c.id === selectedCustomerId) || null;
    }
    return customers[0] || null;
  }, [customers, searchPhone, selectedCustomerId]);

  useEffect(() => {
    if (matchedCustomer?.id) {
      setSelectedCustomerId(matchedCustomer.id);
    }
  }, [matchedCustomer?.id]);

  const detectPromoForCustomer = useCallback(async (customerId) => {
    const hasResolved = Object.prototype.hasOwnProperty.call(promoByCustomer, String(customerId || ''));
    if (!customerId || hasResolved) return;
    setCheckingPromo(true);
    try {
      if (!campaignListRef.current) {
        const listPayload = await apiMerchantCampaigns('all');
        campaignListRef.current = Array.isArray(listPayload?.campaigns)
          ? listPayload.campaigns
              .filter((c) => String(c?.campaign_code || '').trim())
              .slice(0, 6)
          : [];
      }

      const campaigns = campaignListRef.current || [];
      let found = null;
      for (const campaign of campaigns) {
        const campaignId = String(campaign?.id || '').trim();
        if (!campaignId) continue;

        let detail = campaignDetailCacheRef.current[campaignId];
        if (!detail) {
          try {
            detail = await apiMerchantCampaignDetail(campaignId);
            campaignDetailCacheRef.current[campaignId] = detail;
          } catch {
            detail = null;
          }
        }
        if (!detail?.deliveries?.length) continue;

        const hasDelivery = detail.deliveries.some((d) => {
          const sameCustomer = String(d?.customer_id || '').trim() === customerId;
          const status = String(d?.status || '').toLowerCase();
          return sameCustomer && ['sent', 'delivered', 'queued'].includes(status);
        });

        if (hasDelivery) {
          found = {
            code: String(campaign?.campaign_code || '').trim(),
            title: String(campaign?.campaign_name || campaign?.campaign_subtype || '').trim(),
          };
          break;
        }
      }

      if (!mountedRef.current) return;
      setPromoByCustomer((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, customerId)) return prev;
        return {
          ...prev,
          [customerId]: found || null,
        };
      });
    } finally {
      if (mountedRef.current) setCheckingPromo(false);
    }
  }, [promoByCustomer]);

  useEffect(() => {
    if (!matchedCustomer?.id) return;
    detectPromoForCustomer(matchedCustomer.id);
  }, [detectPromoForCustomer, matchedCustomer?.id]);

  const bestBenefit = useMemo(() => {
    if (!matchedCustomer) return null;
    const promo = promoByCustomer[matchedCustomer.id] || null;
    return resolveBestBenefit(matchedCustomer, promo);
  }, [matchedCustomer, promoByCustomer]);

  const runRedeemNow = async () => {
    if (!matchedCustomer) {
      setToastMessage(`❌ ${t('Customer not found')}`);
      return;
    }
    if (!bestBenefit) {
      setToastMessage(`⚠️ ${t('No rewards or points available')}`);
      return;
    }
    if (!bestBenefit.points || bestBenefit.points < 100) {
      setToastMessage(`⚠️ ${t('Promo detected. Ask guest to redeem promo in app checkout.')}`);
      return;
    }

    setBusyAction(true);
    try {
      const res = await executeAIAction(AI_ACTION_TYPES.REDEEM, {
        customerId: matchedCustomer.id,
        customerName: matchedCustomer.name,
        customerPoints: matchedCustomer.points,
        points: bestBenefit.points,
        apply_bonus_on_redemption: applyBonusOnRedemption,
        type: 'BEST_AVAILABLE',
      });
      setToastMessage(`✅ ${res.message}`);
      await fetchCustomers(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('No reward or points available')}`);
    }
    setBusyAction(false);
  };

  const runSimulateOrder = async () => {
    if (!selectedSimulateCustomerId) return;
    const cleanOrderId = (orderId || '').trim().toUpperCase();
    if (!/^ORD-\d{3}$/.test(cleanOrderId)) {
      setToastMessage(`❌ ${t('Order ID must follow ORD-123 format.')}`);
      return;
    }
    const amountNumber = Number(amount);
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setToastMessage(`❌ ${t('Please enter a valid order amount.')}`);
      return;
    }

    setBusySimulate(true);
    try {
      const res = await apiSimulateOrder(selectedSimulateCustomerId, cleanOrderId, amountNumber);
      openEarnModal?.(res);
      setOrderId('');
      setAmount('');
      await fetchCustomers(true);
      setToastMessage(`✅ ${t('Points credited successfully.')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Unable to credit points right now.')}`);
    }
    setBusySimulate(false);
  };

  const openTapSimulation = () => {
    if (!matchedCustomer?.id) {
      setToastMessage(`❌ ${t('Customer not found')}`);
      return;
    }
    const tapPayload = {
      customerId: matchedCustomer.id,
      customerName: matchedCustomer.name || 'Customer',
      customerPoints: Math.max(Number(matchedCustomer.points || 0), 0),
      applyBonusOnRedemption,
    };
    try {
      sessionStorage.setItem('tap_redemption_customer', JSON.stringify(tapPayload));
    } catch {
      // Ignore storage failures and continue with route state.
    }
    navigate(`/merchant/redemptions/tap?customer_id=${encodeURIComponent(matchedCustomer.id)}`, {
      state: tapPayload,
    });
  };

  const approvePending = async (pendingId) => {
    if (!pendingId) return;
    setBusyQueueAction(String(pendingId));
    try {
      await apiValidateRedemption('3457', pendingId);
      await fetchCustomers(true);
      setToastMessage(`✅ ${t('Request approved.')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Validation failed.')}`);
    }
    setBusyQueueAction('');
  };

  const rejectPending = async (pendingId) => {
    if (!pendingId) return;
    setBusyQueueAction(`reject-${pendingId}`);
    try {
      await apiRejectRedemption(pendingId);
      await fetchCustomers(true);
      setRejectedPendingIds((prev) => ({ ...prev, [pendingId]: true }));
      setToastMessage(`✅ ${t('Request rejected.')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Unable to reject request right now.')}`);
    }
    setBusyQueueAction('');
  };

  return (
    <div className="space-y-5" data-testid="merchant-redemptions-page">
      <style>{`
        .no-spinner::-webkit-outer-spin-button,
        .no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner {
          -moz-appearance: textfield;
        }
        @keyframes flybackAiTipGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(var(--brand-rgb), 0.06), 0 8px 24px rgba(var(--brand-rgb), 0.10);
          }
          50% {
            box-shadow: 0 0 0 1px rgba(var(--brand-rgb), 0.35), 0 14px 36px rgba(var(--brand-rgb), 0.22);
          }
        }
        @keyframes flybackAiTipBorderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes flybackAiSparkPulse {
          0% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 0 rgba(var(--brand-rgb), 0)); }
          50% { transform: scale(1.14) rotate(10deg); filter: drop-shadow(0 0 12px rgba(var(--brand-rgb), 0.45)); }
          100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 0 rgba(var(--brand-rgb), 0)); }
        }
      `}</style>
      <CenteredStatusPopup message={toast} />

      <div>
        <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          {t('Redemptions')}
        </h1>
        <p className="text-sm text-stone-500">{t('Detect customer, apply best available benefit, and redeem in one tap.')}</p>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchPhone}
            onChange={(e) => setSearchPhone(e.target.value)}
            placeholder={t('Search by phone number')}
            className="h-11 rounded-xl pl-10"
            data-testid="redemptions-search-phone"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="rounded-xl border border-brand/20 bg-gradient-to-br from-brand-soft to-white p-4">
            {!matchedCustomer && !loading ? (
              <p className="text-sm text-rose-600 font-semibold">❌ {t('Customer not found')}</p>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wider text-brand font-semibold">{t('Auto detected customer')}</p>
                <p className="text-lg font-bold text-[#0f172a] mt-1">
                  {matchedCustomer?.name || (loading ? t('Searching...') : t('No customer found'))}
                  {matchedCustomer?.phone ? <span className="text-sm font-medium text-stone-500 ml-2">({matchedCustomer.phone})</span> : null}
                </p>

                <div className="mt-3 rounded-lg border border-stone-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">🎁 {t('Best Available Benefit')}</p>
                  <p className="text-base font-bold text-[#0f172a] mt-1">
                    {bestBenefit?.title || (loading ? t('Detecting...') : t('No rewards or points available'))}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {checkingPromo ? t('Checking promo availability...') : (bestBenefit?.subtitle || t('Search another customer.'))}
                  </p>
                </div>
              </>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={runRedeemNow}
                disabled={busyAction || !matchedCustomer || !bestBenefit || loading}
                className="h-11 px-5 rounded-xl gradient-brand-btn text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="redemptions-redeem-now"
              >
                {busyAction ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('Redeem Now')}
              </button>

              <div
                className="flex-1 min-w-[250px] rounded-xl border px-3 py-2"
                style={{
                  borderColor: 'rgba(var(--brand-rgb), 0.30)',
                  background: 'linear-gradient(135deg, rgba(var(--brand-rgb), 0.16), rgba(255,255,255,0.98), rgba(var(--brand-rgb), 0.08))',
                  backgroundSize: '220% 220%',
                  animation: 'flybackAiTipGlow 2.8s ease-in-out infinite, flybackAiTipBorderFlow 4s ease-in-out infinite',
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 rounded-full bg-white items-center justify-center border border-brand/20">
                    <Sparkles className="w-3.5 h-3.5 text-brand" style={{ animation: 'flybackAiSparkPulse 2.1s ease-in-out infinite' }} />
                  </span>
                  <p className="font-semibold text-xs text-brand">{t('AI Tip')}</p>
                </div>
                <p className="mt-1.5 text-xs text-[#0f172a] leading-relaxed">
                  {t('Offering 10% point bonus on redemption increases return visit rate by ~20%.')}
                </p>
                <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-[#0f172a] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyBonusOnRedemption}
                    onChange={(e) => setApplyBonusOnRedemption(e.target.checked)}
                    className="h-4 w-4 rounded border-stone-300 text-brand focus:ring-brand"
                    data-testid="redemptions-apply-bonus-checkbox"
                  />
                  <span>{t('Apply this bonus on redemption')}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{t('Tap to Redeem')}</p>
              <p className="text-sm text-stone-600 mt-1">
                {t('Open tap simulation and complete redemption with one card tap.')}
              </p>
            </div>
            <button
              type="button"
              onClick={openTapSimulation}
              disabled={!matchedCustomer?.id}
              className="mt-4 h-11 px-5 rounded-xl border border-brand/20 bg-brand-soft text-brand text-sm font-semibold inline-flex items-center justify-center gap-2"
              data-testid="redemptions-tap-customer-card"
            >
              <CreditCard className="w-4 h-4" />
              {t('Tap Customer Card')}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-stone-800">{t('Select & Credit')}</p>
          <p className="text-xs text-stone-500">{t('Quickly credit points for an in-person order simulation.')}</p>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1.2fr,0.9fr,0.9fr,auto] gap-3 items-end">
          <CustomerCombobox
            customers={customers}
            value={selectedSimulateCustomerId}
            onChange={setSelectedSimulateCustomerId}
            placeholder={t('Select customer...')}
            searchPlaceholder={t('Search customer')}
            emptyText={t('No customer found.')}
            testId="redemptions-simulate-customer-select"
          />
          <Input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value.toUpperCase())}
            placeholder={t('ORD-123')}
            className="h-10 rounded-xl border-stone-200"
          />
          <Input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            placeholder={t('Amount')}
            className="no-spinner h-10 rounded-xl border-stone-200"
          />
          <button
            type="button"
            onClick={runSimulateOrder}
            disabled={busySimulate || loading}
            className="h-10 px-4 rounded-xl gradient-brand-btn text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busySimulate ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t('Credit Points')}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-stone-800">{t('Customer Redemption Queue')}</p>
            <p className="text-xs text-stone-500">{t('Approve or reject guest redemption requests in one tap.')}</p>
          </div>
          <Badge className="rounded-full bg-brand-soft text-brand border border-brand/20">
            {queue.length}
          </Badge>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-100">
                {[t('Customer'), t('Requested Points'), t('Channel'), t('Created At'), t('Actions')].map((header) => (
                  <th
                    key={header}
                    className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-stone-500"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!queue.length ? (
                <tr>
                  <td colSpan={5} className="px-3 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-stone-500">
                      <span className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center">
                        <Inbox className="w-5 h-5 text-stone-400" />
                      </span>
                      <p className="text-sm">{t('No pending redemption requests.')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                queue.map((row) => (
                  <tr key={row.id} className="border-b border-stone-100">
                    <td className="px-3 py-2.5">{row.customer_name || t('N/A')}</td>
                    <td className="px-3 py-2.5">{Number(row.points || 0).toLocaleString()} {t('pts')}</td>
                    <td className="px-3 py-2.5 capitalize">{row.channel || 'source'}</td>
                    <td className="px-3 py-2.5">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : t('N/A')}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => approvePending(row.id)}
                          disabled={busyQueueAction === String(row.id)}
                          className="h-8 px-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                        >
                          {busyQueueAction === String(row.id) ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                          {t('Approve')}
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectPending(row.id)}
                          disabled={busyQueueAction === `reject-${row.id}`}
                          className="h-8 px-2.5 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold disabled:opacity-60"
                        >
                          {busyQueueAction === `reject-${row.id}` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            t('Reject')
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
